/**
 * B080020 出荷作業指示一覧
 */

import { Column, Query } from "@material-table/core";
import { Box } from "@mui/material";
import {
  B080020Find,
  B080020FindRequest,
  B080020Print,
  B080020PrintDetailRequest,
  B080020PrintRequest,
  catchFileError,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SConfirmDialog,
  SDateField,
  SFileButton,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
} from "components";
import { STheme } from "components/styles";
import { getClientError, getClientMessage } from "messages";
import {
  B080020SagyohakoView,
  getSCode,
  HyoziShukajokyo,
} from "messages/codes";
import { labels } from "messages/labels";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { date, dateWithSlashAt, download, mapIds, printed } from "services";
import { cancelKbn, sagyohako, sutiShukajokyo } from "services/codes";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  shukashijiYmd: string;
  shijikaisu: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  shukashijiYmd: string;
  shijikaisu: number;
  batchNo: number;
  batchNm: string;
  tenposu: number;
  shohinsu: number;
  denpyomaisu: number;
  denpyogyosu: number;
  ppshijisu: number;
  netsukeshijisu: number;
  setshohinsu: number;
  shukajokyo: number;
  sagyohako: string;
  cancelKbn: number;
  cancelKbnView: string;
  version: number;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  shukashijiYmd: date(),
  shijikaisu: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  {
    field: "shukashijiYmd",
    title: "出荷指示日",
    width: "5rem",
    render: (data) => {
      if (data.shukashijiYmd! !== null && data.shukashijiYmd !== "") {
        return dateWithSlashAt(data.shukashijiYmd!);
      }
    },
  },
  {
    field: "shijikaisu",
    title: "指示回数",
    width: "3rem",
    align: "right",
    render: (data) => data.shijikaisu?.toLocaleString(),
  },
  { field: "batchNo", title: "バッチ番号", width: "4rem", align: "right" },
  { field: "batchNm", title: "バッチ名", width: "12rem" },
  {
    field: "tenposu",
    title: "店舗数　",
    width: "3rem",
    align: "right",
    render: (data) => data.tenposu?.toLocaleString(),
  },
  {
    field: "shohinsu",
    title: "商品数　",
    width: "4rem",
    align: "right",
    render: (data) => data.shohinsu?.toLocaleString(),
  },
  {
    field: "denpyomaisu",
    title: "伝票枚数　",
    width: "3rem",
    align: "right",
    render: (data) => data.denpyomaisu?.toLocaleString(),
  },
  {
    field: "denpyogyosu",
    title: "伝票行数　",
    width: "3rem",
    align: "right",
    render: (data) => data.denpyogyosu?.toLocaleString(),
  },
  {
    field: "ppshijisu",
    title: "PPがけ",
    width: "3rem",
    align: "right",
    render: (data) => data.ppshijisu?.toLocaleString(),
  },
  {
    field: "netsukeshijisu",
    title: "値付け",
    width: "3rem",
    align: "right",
    render: (data) => data.netsukeshijisu?.toLocaleString(),
  },
  {
    field: "setshohinsu",
    title: "セット品",
    width: "3rem",
    align: "right",
    render: (data) => data.setshohinsu?.toLocaleString(),
  },
  {
    field: "shukajokyo",
    title: "出荷状況",
    width: "6rem",
    align: "center",
    render: (data) => {
      const text = getSCode(
        HyoziShukajokyo,
        data.shukajokyo?.toString() ?? "0"
      )?.label;
      // 未処理
      let color = STheme.text.red;
      if (data.shukajokyo === sutiShukajokyo.shijizumi) {
        // 指示済み
        color = STheme.text.blue;
      } else if (data.shukajokyo === sutiShukajokyo.kakuteizumi) {
        // 確定済み
        color = STheme.text.green;
      }
      return <SText color={color}>{text}</SText>;
    },
  },
  {
    field: "cancelKbnView",
    title: "キャンセル有",
    width: "4rem",
    align: "center",
    render: (data) => {
      if (data.cancelKbn === cancelKbn.sumi) {
        return "◯";
      }
      if (data.cancelKbn === cancelKbn.zen) {
        return "全";
      }
      return "";
    },
  },
  { field: "cancelKbn", hidden: true },
  {
    field: "sagyohako",
    title: "作業指示書発行状況",
    width: "6rem",
    render: (data) => {
      const text = getSCode(
        B080020SagyohakoView,
        data.sagyohako?.toString() ?? "0"
      );
      // 未発行
      let color = STheme.text.red;
      // 発行済
      if (data.sagyohako === sagyohako.hakozumi) {
        color = STheme.text.blue;
      }
      return <SText color={color}>{text?.label}</SText>;
    },
  },
  { field: "version", hidden: true },
];

const focusId = "B080020";

// B080020 出荷作業指示一覧
export const B080020 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  const pageSize = 300;
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  // selectboxで選択された行のデータリスト
  const [selectedData, setSelectedData] = useState<Data[]>([]);
  // 印刷ボタン活性制御
  const [disabledPrintButton, setDisabledPrintButton] = useState(true);

  // 初期表示イベント
  useEffect(() => {
    // 初回フォーカス先
    focus(1);
  });

  // 検索時の初期化処理
  const handleResetSearch = () => {
    // ボタン非活性化
    setDisabledPrintButton(true);
    // 選択されているデータを空にする
    setSelectedData([]);
    (tableRef.current as any).handleClearSelect();
  };

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 初期化
    handleResetSearch();
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // テーブル行チェックボックス選択時
  const handleCheckTableRow = (data: Data[]) => {
    // 行が選択されている場合のみ実行する。
    const disabled = data.length <= 0;
    // 印刷ボタン活性化
    setDisabledPrintButton(disabled);

    // 選択データリストにデータ追加
    setSelectedData(data);
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B080020FindRequest = {
        shukashijiYmd: fields.shukashijiYmd!,
        shijikaisu: fields.shijikaisu,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: ["shukashijiYmd,asc", "shijikaisu,asc", "batchNo,asc"],
      };
      // API呼出し
      B080020Find(param)
        // 正常値取得時
        .then((value) => {
          if (value.data.totalElements === 0) {
            setMsg(getClientError("B080020_001"));
          }
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // 印刷イベント
  const handlePrint = () => {
    // Excel出力フォーム用のデータを取得する。
    const list = selectedData;
    // Excel出力パラメータを作成する。
    const param: B080020PrintRequest = {
      b080020PrintDetailRequestlist: list.map<B080020PrintDetailRequest>(
        (value) => ({
          shukashijiYmd: value.shukashijiYmd!,
          shijikaisu: value.shijikaisu!.toString(),
          batchNo: value.batchNo!.toString(),
          shukajokyo: value.shukajokyo!,
          version: value.version!.toString(),
        })
      ),
    };
    B080020Print(param)
      .then((response) => {
        //テーブル更新の為再検索
        (tableRef.current as any).onQueryChange();
        // ボタン非活性化
        setDisabledPrintButton(true);
        // 選択されているデータを空にする
        setSelectedData([]);
        if (printed(response)) {
          setMsg(getClientMessage("B080020_001"));
        } else {
          download(response);
          setMsg(getClientMessage("B080020_002"));
        }
      })
      .catch((reason) => {
        catchFileError(reason, navigate, setMsg);
      });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="出荷作業指示一覧"
        gridTemplateColumns="6rem 8rem 3rem 6rem 5rem"
      >
        <SLabel required>出荷指示日</SLabel>
        <SDateField
          name="shukashijiYmd"
          label="出荷指示日"
          required
          {...register(1)}
        />

        <Box />

        <SLabel>指示回数</SLabel>
        <SNumberField name="shijikaisu" size={4} {...register(2)} />

        <Box gridColumn="span 4" />

        <SNormalButton
          label={labels.buttons.search}
          align="right"
          {...register(4)}
          onClick={handleClickSearchButton}
        />
      </SFrame>
      <SFrame>
        <STable
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          onSelectionChange={handleCheckTableRow}
          multi
          handleSetDisabled={(rowData) =>
            rowData.shukajokyo === sutiShukajokyo.kakuteizumi ||
            rowData.cancelKbn === cancelKbn.zen
          }
          width={1265}
          height={440}
          pageSize={pageSize}
          onPageChange={handleResetSearch}
        />
      </SFrame>
      <SFrame>
        <SFileButton
          label={labels.buttons.print}
          align="right"
          {...register(4)}
          disabled={disabledPrintButton}
          dialog={
            <SConfirmDialog title="印刷" msg="印刷します。よろしいですか？" />
          }
          dialogAction="confirm"
          handleSubmit={handlePrint}
        />
      </SFrame>
    </FormProvider>
  );
};
